/* global PiniaSetupStoreHelpers */
import type { SubmitEventPromise } from 'vuetify';
import type { RequiredKeys } from '@/utils/types';
import type { DialogElement } from '@/components/SmartAppDialogs.vue';
import type { ValidData as AddAddressDialogFormValidData } from '@/components/dialogForms/AddAddressDialogForm.vue';
import type { ValidData as AddressBookAddressDialogFormValidData } from '@/components/dialogForms/AddressBookAddressDialogForm.vue';
import type { ValidData as AdvancedSearchMultiSearchDialogFormValidData } from '@/components/dialogForms/AdvancedSearchMultiSearchDialogForm.vue';
import type { ValidData as CancelCloseStopDialogFormValidData } from '@/components/dialogForms/CancelCloseStopDialogForm.vue';
import type { ValidData as ChooseOptionStopDialogFormValidData } from '@/components/dialogForms/ChooseOptionStopDialogForm.vue';
import type { ValidData as DashboardStopDialogFormValidData } from '@/components/dialogForms/DashboardStopDialogForm.vue';
import type { ValidData as DispatchAssignDriverDialogFormValidData } from '@/components/dialogForms/DispatchAssignDriverDialogForm.vue';
import type { ValidData as DriverReportGenerateDialogFormValidData } from '@/components/dialogForms/DriverReportGenerateDialogForm.vue';
import type { ValidData as EditTimestampsDialogFormValidData } from '@/components/dialogForms/EditTimestampsDialogForm.vue';
import type { ValidData as JobEditCancelJobDialogFormValidData } from '@/components/dialogForms/JobEditCancelJobDialogForm.vue';
import type { ValidData as JobEditChangeCustomerCodeDialogFormValidData } from '@/components/dialogForms/JobEditChangeCustomerCodeDialogForm.vue';
import type { ValidData as JobEditCloseAsFutileDialogFormValidData } from '@/components/dialogForms/JobEditCloseAsFutileDialogForm.vue';
import type { ValidData as JobEditSplitToBaseDialogFormValidData } from '@/components/dialogForms/JobEditSplitToBaseDialogForm.vue';
import type { ValidData as KpiReportGenerateDialogFormValidData } from '@/components/dialogForms/KpiReportGenerateDialogForm.vue';
import type { ValidData as NewAirRoadUnpackDialogFormValidData } from '@/components/dialogForms/NewAirRoadUnpackDialogForm.vue';
import type { ValidData as NewMessageChatDialogFormValidData } from '@/components/dialogForms/NewMessageChatDialogForm.vue';
import type { ValidData as NewQueryDialogFormValidData } from '@/components/dialogForms/NewQueryDialogForm.vue';
import type { ValidData as NewRFCDialogFormValidData } from '@/components/dialogForms/NewRFCDialogForm.vue';
import type { ValidData as NewTicketDialogFormValidData } from '@/components/dialogForms/NewTicketDialogForm.vue';
import type { ValidData as ReturnLabelsPrintDialogFormValidData } from '@/components/dialogForms/ReturnLabelsPrintDialogForm.vue';
import type { ValidData as SavingCurrentSearchDialogFormValidData } from '@/components/dialogForms/SavingCurrentSearchDialogForm.vue';
import type { ValidData as SplitToBaseDialogFormValidData } from '@/components/dialogForms/SplitToBaseDialogForm.vue';
import type { ValidData as UploadingFilesDialogFormValidData } from '@/components/dialogForms/UploadingFilesDialogForm.vue';
import type {
  DefProps as AddAddressDialogDefProps,
  Props as AddAddressDialogProps,
} from '@/components/dialogs/AddAddressDialog.vue';
import type {
  DefProps as AddressBookAddressDialogDefProps,
  Props as AddressBookAddressDialogProps,
} from '@/components/dialogs/AddressBookAddressDialog.vue';
import type {
  DefProps as AddressBookDeleteAddressDialogDefProps,
  Props as AddressBookDeleteAddressDialogProps,
} from '@/components/dialogs/AddressBookDeleteAddressDialog.vue';
import type {
  DefProps as AdvancedSearchMultiSearchDialogDefProps,
  Props as AdvancedSearchMultiSearchDialogProps,
} from '@/components/dialogs/AdvancedSearchMultiSearchDialog.vue';
import type {
  DefProps as ArrivedDepartedStopDialogDefProps,
  Props as ArrivedDepartedStopDialogProps,
} from '@/components/dialogs/ArrivedDepartedStopDialog.vue';
import type {
  DefProps as CancelCloseStopDialogDefProps,
  Props as CancelCloseStopDialogProps,
} from '@/components/dialogs/CancelCloseStopDialog.vue';
import type {
  DefProps as ChooseOptionStopDialogDefProps,
  Props as ChooseOptionStopDialogProps,
} from '@/components/dialogs/ChooseOptionStopDialog.vue';
import type {
  DefProps as CloneJobDialogDefProps,
  Props as CloneJobDialogProps,
} from '@/components/dialogs/CloneJobDialog.vue';
import type { Props as ConfirmScrapBookingDialogProps } from '@/components/dialogs/ConfirmScrapBookingDialog.vue';
import type {
  DefProps as DeleteJobPhotoDialogDefProps,
  Props as DeleteJobPhotoDialogProps,
} from '@/components/dialogs/DeleteJobPhotoDialog.vue';
import type { Props as DetailedJobPhotoDialogProps } from '@/components/dialogs/DetailedJobPhotoDialog.vue';
import type {
  DefProps as DispatchAssignDriverDialogDefProps,
  Props as DispatchAssignDriverDialogProps,
} from '@/components/dialogs/DispatchAssignDriverDialog.vue';
import type {
  DefProps as DispatchConfirmDialogDefProps,
  Props as DispatchConfirmDialogProps,
} from '@/components/dialogs/DispatchConfirmDialog.vue';
import type {
  DefProps as DriverReportGenerateDialogDefProps,
  Props as DriverReportGenerateDialogProps,
} from '@/components/dialogs/DriverReportGenerateDialog.vue';
import type {
  DefProps as EditTimestampsDialogDefProps,
  Props as EditTimestampsDialogProps,
} from '@/components/dialogs/EditTimestampsDialog.vue';
import type {
  DefProps as JobEditCancelJobDialogDefProps,
  Props as JobEditCancelJobDialogProps,
} from '@/components/dialogs/JobEditCancelJobDialog.vue';
import type {
  DefProps as JobEditChangeCustomerCodeDialogDefProps,
  Props as JobEditChangeCustomerCodeDialogProps,
} from '@/components/dialogs/JobEditChangeCustomerCodeDialog.vue';
import type {
  DefProps as JobEditCloseAsFutileDialogDefProps,
  Props as JobEditCloseAsFutileDialogProps,
} from '@/components/dialogs/JobEditCloseAsFutileDialog.vue';
import type {
  DefProps as JobEditDeleteStopDialogDefProps,
  Props as JobEditDeleteStopDialogProps,
} from '@/components/dialogs/JobEditDeleteStopDialog.vue';
import type {
  DefProps as JobEditErrorDialogDefProps,
  Props as JobEditErrorDialogProps,
} from '@/components/dialogs/JobEditErrorDialog.vue';
import type {
  DefProps as JobEditLockJobDialogDefProps,
  Props as JobEditLockJobDialogProps,
} from '@/components/dialogs/JobEditLockJobDialog.vue';
import type {
  DefProps as JobEditLockTimeoutDialogDefProps,
  Props as JobEditLockTimeoutDialogProps,
} from '@/components/dialogs/JobEditLockTimeoutDialog.vue';
import type { Props as JobEditLockedByOtherDialogProps } from '@/components/dialogs/JobEditLockedByOtherDialog.vue';
import type { Props as JobEditNotFoundDialogProps } from '@/components/dialogs/JobEditNotFoundDialog.vue';
import type {
  DefProps as JobEditSplitToBaseDialogDefProps,
  Props as JobEditSplitToBaseDialogProps,
} from '@/components/dialogs/JobEditSplitToBaseDialog.vue';
import type {
  DefProps as JobEditUndoAllChangesDialogDefProps,
  Props as JobEditUndoAllChangesDialogProps,
} from '@/components/dialogs/JobEditUndoAllChangesDialog.vue';
import type {
  DefProps as KpiReportGenerateDialogDefProps,
  Props as KpiReportGenerateDialogProps,
} from '@/components/dialogs/KpiReportGenerateDialog.vue';
import type {
  DefProps as LogoutDialogDefProps,
  Props as LogoutDialogProps,
} from '@/components/dialogs/LogoutDialog.vue';
import type {
  DefProps as NewAirRoadUnpackDialogDefProps,
  Props as NewAirRoadUnpackDialogProps,
} from '@/components/dialogs/NewAirRoadUnpackDialog.vue';
import type {
  DefProps as NewMessageChatDialogDefProps,
  Props as NewMessageChatDialogProps,
} from '@/components/dialogs/NewMessageChatDialog.vue';
import type {
  DefProps as NewQueryDialogDefProps,
  Props as NewQueryDialogProps,
} from '@/components/dialogs/NewQueryDialog.vue';
import type {
  DefProps as NewRFCDialogDefProps,
  Props as NewRFCDialogProps,
} from '@/components/dialogs/NewRFCDialog.vue';
import type {
  DefProps as NewTicketDialogDefProps,
  Props as NewTicketDialogProps,
} from '@/components/dialogs/NewTicketDialog.vue';
import type {
  DefProps as PrintDialogDefProps,
  Props as PrintDialogProps,
} from '@/components/dialogs/PrintDialog.vue';
import type {
  DefProps as ReturnLabelsPrintDialogDefProps,
  Props as ReturnLabelsPrintDialogProps,
} from '@/components/dialogs/ReturnLabelsPrintDialog.vue';
import type {
  DefProps as SaveJobDraftDialogDefProps,
  Props as SaveJobDraftDialogProps,
  SaveDraftMode,
} from '@/components/dialogs/SaveJobDraftDialog.vue';
import type { Props as SavedFiltersDialogProps } from '@/components/dialogs/SavedFiltersDialog.vue';
import type {
  DefProps as SavingCurrentSearchDialogDefProps,
  Props as SavingCurrentSearchDialogProps,
} from '@/components/dialogs/SavingCurrentSearchDialog.vue';
import type {
  DefProps as SplitToBaseDialogDefProps,
  Props as SplitToBaseDialogProps,
} from '@/components/dialogs/SplitToBaseDialog.vue';
import type {
  DefProps as UploadingFilesDialogDefProps,
  Props as UploadingFilesDialogProps,
} from '@/components/dialogs/UploadingFilesDialog.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { reactive, ref, toRefs, watch } from 'vue';
import { defineStore } from 'pinia';
import { useAbility } from '@casl/vue';
import EventEmitter from 'eventemitter3';
import _cloneDeep from 'lodash/cloneDeep'; // use `_cloneDeep` to avoid accidentally changing `defState`

import { cleanEmailString } from '@/utils/Common';
import { kktsSplitToBase } from '@/utils/PortalConstants';
import { successDelay } from '@/utils/funcs';
import { getBase64FileForZammad } from '@/utils/zammad';
import { useUserStore } from '@/stores/useUserStore';
import {
  fetchPostAirroadJobsByIdPrintLabel,
  fetchPostJobAllocation,
  fetchPostJobAllocationBulk,
  fetchPostJobCancel,
  fetchPostJobFutile,
  fetchPostJobMessage,
  fetchPostReportReturnLabelsPrint,
  fetchPostZammadTickets,
} from '@/helpers/fetchers';
import { KDialogError } from '@/helpers/throwables';

export interface DefDialogState {
  logout: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<LogoutDialogProps>;
  }>;
  dispatchAssignDriver: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<DispatchAssignDriverDialogProps>;
  }>;
  dispatchConfirm: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<DispatchConfirmDialogProps>;
  }>;
  arrivedDepartedStop: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<ArrivedDepartedStopDialogProps>;
  }>;
  chooseOptionStop: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<ChooseOptionStopDialogProps>;
  }>;
  cancelCloseStop: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<CancelCloseStopDialogProps>;
  }>;
  newTicket: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<NewTicketDialogProps>;
  }>;
  newRFC: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<NewRFCDialogProps>;
  }>;
  cloneJob: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<CloneJobDialogProps>;
  }>;
  newQuery: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<NewQueryDialogProps>;
  }>;
  jobEditCancelJob: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<JobEditCancelJobDialogProps>;
  }>;
  jobEditChangeCustomerCode: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<JobEditChangeCustomerCodeDialogProps>;
  }>;
  jobEditCloseAsFutile: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<JobEditCloseAsFutileDialogProps>;
  }>;
  jobEditDeleteStop: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<JobEditDeleteStopDialogProps>;
  }>;
  jobEditError: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<JobEditErrorDialogProps>;
  }>;
  jobEditLockedByOther: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<JobEditLockedByOtherDialogProps>;
  }>;
  jobEditLockTimeout: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<JobEditLockTimeoutDialogProps>;
  }>;
  jobEditNotFound: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<JobEditNotFoundDialogProps>;
  }>;
  jobEditSplitToBase: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<JobEditSplitToBaseDialogProps>;
  }>;
  jobEditLockJob: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<JobEditLockJobDialogProps>;
  }>;
  jobEditUndoAllChanges: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<JobEditUndoAllChangesDialogProps>;
  }>;
  splitToBase: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<SplitToBaseDialogProps>;
  }>;
  editTimestamps: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<EditTimestampsDialogProps>;
  }>;
  advancedSearchMultiSearch: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<AdvancedSearchMultiSearchDialogProps>;
  }>;
  savedFilters: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'cancel' | undefined;
    dialogData: RequiredKeys<SavedFiltersDialogProps>;
  }>;
  savingCurrentSearch: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<SavingCurrentSearchDialogProps>;
  }>;
  uploadingFiles: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<UploadingFilesDialogProps>;
  }>;
  detailedJobPhoto: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'cancel' | undefined;
    dialogData: RequiredKeys<DetailedJobPhotoDialogProps>;
  }>;
  deleteJobPhoto: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<DeleteJobPhotoDialogProps>;
  }>;
  print: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<PrintDialogProps>;
  }>;
  returnLabelsPrint: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<ReturnLabelsPrintDialogProps>;
  }>;
  newAirRoadUnpack: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<NewAirRoadUnpackDialogProps>;
  }>;
  newMessageChat: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<NewMessageChatDialogProps>;
  }>;
  kpiReportGenerate: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<KpiReportGenerateDialogProps>;
  }>;
  driverReportGenerate: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<DriverReportGenerateDialogProps>;
  }>;
  saveJobDraft: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<SaveJobDraftDialogProps>;
  }>;
  confirmScrapBooking: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<ConfirmScrapBookingDialogProps>;
  }>;
  addAddress: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<AddAddressDialogProps>;
  }>;
  addressBookAddress: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<AddressBookAddressDialogProps>;
  }>;
  addressBookDeleteAddress: RequiredKeys<{
    isDialog: boolean | undefined;
    closeDetail: 'confirm' | 'cancel' | undefined;
    dialogData: RequiredKeys<AddressBookDeleteAddressDialogProps>;
  }>;
}

const defDialogState: DefDialogState = {
  logout: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      confirm: undefined,
    },
  },
  dispatchAssignDriver: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      jobs: undefined,
      action: undefined,
    },
  },
  dispatchConfirm: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      type: undefined,
      isConfirmLoading: undefined,
      confirm: undefined,
      jobs: undefined,
    },
  },
  arrivedDepartedStop: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      confirm: undefined,
      driver: undefined,
      jobId: undefined,
      stopNum: undefined,
      stopNames: undefined,
      isForm: undefined,
      hasArrived: undefined,
      isCompleted: undefined,
    },
  },
  chooseOptionStop: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      confirm: undefined,
      stopNum: undefined,
    },
  },
  cancelCloseStop: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      type: undefined,
      isConfirmLoading: undefined,
      jobs: undefined,
    },
  },
  newTicket: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      jobId: undefined,
      jobDate: undefined,
      opsEmails: undefined,
      channelsEmails: undefined,
    },
  },
  newRFC: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      jobId: undefined,
      jobDate: undefined,
    },
  },
  cloneJob: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      job: undefined,
    },
  },
  newQuery: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      job: undefined,
    },
  },
  jobEditCancelJob: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      jobId: undefined,
      jobDate: undefined,
      actor: undefined,
      reason: undefined,
      confirm: undefined,
    },
  },
  jobEditChangeCustomerCode: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      customerCode: undefined,
      confirm: undefined,
    },
  },
  jobEditCloseAsFutile: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      jobId: undefined,
      jobDate: undefined,
      reason: undefined,
      confirm: undefined,
    },
  },
  jobEditDeleteStop: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      stopNumber: undefined,
      confirm: undefined,
    },
  },
  jobEditError: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      errorMessage: undefined,
      retryCallback: undefined,
      cancelCallback: undefined,
    },
  },
  jobEditLockedByOther: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      lockedBy: undefined,
    },
  },
  jobEditLockTimeout: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      remainingSeconds: undefined,
      confirm: undefined,
    },
  },
  jobEditNotFound: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      errorMessage: undefined,
    },
  },
  jobEditSplitToBase: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: false,
      stopsCount: 2,
      job: undefined,
      stopNumber: undefined,
      warehouse: undefined,
      confirm: undefined,
    },
  },
  jobEditLockJob: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      isLocked: undefined,
      confirm: undefined,
    },
  },
  jobEditUndoAllChanges: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      confirm: undefined,
    },
  },
  splitToBase: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      job: undefined,
    },
  },
  editTimestamps: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      job: undefined,
    },
  },
  advancedSearchMultiSearch: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      confirm: undefined,
    },
  },
  savedFilters: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      deleteCompanyFilter: undefined,
      deleteMyFilter: undefined,
    },
  },
  savingCurrentSearch: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      confirm: undefined,
    },
  },
  uploadingFiles: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      confirm: undefined,
    },
  },
  detailedJobPhoto: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      image: undefined,
    },
  },
  deleteJobPhoto: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      image: undefined,
      isConfirmLoading: undefined,
      confirm: undefined,
    },
  },
  print: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      arJobId: undefined,
      arJobConnote: undefined,
    },
  },
  returnLabelsPrint: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      drivers: undefined,
    },
  },
  newAirRoadUnpack: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      confirm: undefined,
      packageNum: undefined,
      productNum: undefined,
      allUnpackCount: undefined,
      addProductId: undefined,
      maxQuantity: undefined,
    },
  },
  newMessageChat: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      confirm: undefined,
    },
  },
  kpiReportGenerate: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      confirm: undefined,
      customer: undefined,
    },
  },
  driverReportGenerate: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      confirm: undefined,
    },
  },
  saveJobDraft: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      initialName: undefined,
      isEditingDraft: undefined,
      confirm: undefined,
    },
  },
  confirmScrapBooking: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
    },
  },
  addAddress: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isConfirmLoading: undefined,
      confirm: undefined,
    },
  },
  addressBookAddress: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      isEdit: undefined,
      isConfirmLoading: undefined,
      initialData: undefined,
      confirm: undefined,
    },
  },
  addressBookDeleteAddress: {
    isDialog: undefined,
    closeDetail: undefined,
    dialogData: {
      address: undefined,
      isConfirmLoading: undefined,
      confirm: undefined,
    },
  },
};

interface FunctionParams {
  logout: LogoutDialogProps;
  dispatchAssignDriver: DispatchAssignDriverDialogProps;
  dispatchConfirm: DispatchConfirmDialogProps;
  arrivedDepartedStop: ArrivedDepartedStopDialogProps;
  chooseOptionStop: ChooseOptionStopDialogProps;
  cancelCloseStop: CancelCloseStopDialogProps;
  newTicket: NewTicketDialogProps;
  newRFC: NewRFCDialogProps;
  cloneJob: CloneJobDialogProps;
  newQuery: NewQueryDialogProps;
  jobEditCancelJob: JobEditCancelJobDialogProps;
  jobEditChangeCustomerCode: JobEditChangeCustomerCodeDialogProps;
  jobEditCloseAsFutile: JobEditCloseAsFutileDialogProps;
  jobEditDeleteStop: JobEditDeleteStopDialogProps;
  jobEditError: JobEditErrorDialogProps;
  jobEditLockedByOther: JobEditLockedByOtherDialogProps;
  jobEditLockTimeout: JobEditLockTimeoutDialogProps;
  jobEditNotFound: JobEditNotFoundDialogProps;
  jobEditSplitToBase: JobEditSplitToBaseDialogProps;
  jobEditLockJob: JobEditLockJobDialogProps;
  jobEditUndoAllChanges: JobEditUndoAllChangesDialogProps;
  splitToBase: SplitToBaseDialogProps;
  editTimestamps: EditTimestampsDialogProps;
  advancedSearchMultiSearch: AdvancedSearchMultiSearchDialogProps;
  savedFilters: SavedFiltersDialogProps;
  savingCurrentSearch: SavingCurrentSearchDialogProps;
  uploadingFiles: UploadingFilesDialogProps;
  detailedJobPhoto: DetailedJobPhotoDialogProps;
  deleteJobPhoto: DeleteJobPhotoDialogProps;
  print: PrintDialogProps;
  returnLabelsPrint: ReturnLabelsPrintDialogProps;
  newAirRoadUnpack: NewAirRoadUnpackDialogProps;
  newMessageChat: NewMessageChatDialogProps;
  kpiReportGenerate: KpiReportGenerateDialogProps;
  driverReportGenerate: DriverReportGenerateDialogProps;
  saveJobDraft: SaveJobDraftDialogProps;
  confirmScrapBooking: ConfirmScrapBookingDialogProps;
  addAddress: AddAddressDialogProps;
  addressBookAddress: AddressBookAddressDialogProps;
  addressBookDeleteAddress: AddressBookDeleteAddressDialogProps;
}
type FunctionName = keyof FunctionParams;

export type DialogName = keyof DefDialogState;

type DialogEventBus = EventEmitter & {
  confirmedCb?: () => Promise<void>;
  canceledCb?: () => Promise<void>;
  confirmed: (cb: () => Promise<void>) => DialogEventBus;
  canceled: (cb: () => Promise<void>) => DialogEventBus;
};

function makeDialogEventBus(): DialogEventBus {
  const _eventBus = Object.assign(new EventEmitter(), {
    confirmed: function () {
      return _eventBus;
    },
    canceled: function () {
      return _eventBus;
    },
  });
  return _eventBus;
}

function dialogStoreSetup(helpers: PiniaSetupStoreHelpers) {
  const state = reactive(_cloneDeep(defDialogState));

  const userStore = useUserStore();
  const ability = useAbility();

  const _logoutDialogRef = ref<DialogElement | null>(null);
  const _dispatchAssignDriverDialogRef = ref<DialogElement | null>(null);
  const _dispatchConfirmDialogRef = ref<DialogElement | null>(null);
  const _arrivedDepartedStopDialogRef = ref<DialogElement | null>(null);
  const _chooseOptionStopDialogRef = ref<DialogElement | null>(null);
  const _cancelCloseStopDialogRef = ref<DialogElement | null>(null);
  const _newTicketDialogRef = ref<DialogElement | null>(null);
  const _newRFCDialogRef = ref<DialogElement | null>(null);
  const _cloneJobDialogRef = ref<DialogElement | null>(null);
  const _newQueryDialogRef = ref<DialogElement | null>(null);
  const _jobEditCancelJobDialogRef = ref<DialogElement | null>(null);
  const _jobEditChangeCustomerCodeDialogRef = ref<DialogElement | null>(null);
  const _jobEditCloseAsFutileDialogRef = ref<DialogElement | null>(null);
  const _jobEditDeleteStopDialogRef = ref<DialogElement | null>(null);
  const _jobEditErrorDialogRef = ref<DialogElement | null>(null);
  const _jobEditLockedByOtherDialogRef = ref<DialogElement | null>(null);
  const _jobEditLockTimeoutDialogRef = ref<DialogElement | null>(null);
  const _jobEditNotFoundDialogRef = ref<DialogElement | null>(null);
  const _jobEditSplitToBaseDialogRef = ref<DialogElement | null>(null);
  const _jobEditLockJobDialogRef = ref<DialogElement | null>(null);
  const _jobEditUndoAllChangesDialogRef = ref<DialogElement | null>(null);
  const _splitToBaseDialogRef = ref<DialogElement | null>(null);
  const _editTimestampsDialogRef = ref<DialogElement | null>(null);
  const _advancedSearchMultiSearchDialogRef = ref<DialogElement | null>(null);
  const _savedFiltersDialogRef = ref<DialogElement | null>(null);
  const _savingCurrentSearchDialogRef = ref<DialogElement | null>(null);
  const _uploadingFilesDialogRef = ref<DialogElement | null>(null);
  const _detailedJobPhotoDialogRef = ref<DialogElement | null>(null);
  const _deleteJobPhotoDialogRef = ref<DialogElement | null>(null);
  const _printDialogRef = ref<DialogElement | null>(null);
  const _returnLabelsPrintDialogRef = ref<DialogElement | null>(null);
  const _newAirRoadUnpackDialogRef = ref<DialogElement | null>(null);
  const _newMessageChatDialogRef = ref<DialogElement | null>(null);
  const _kpiReportGenerateDialogRef = ref<DialogElement | null>(null);
  const _driverReportGenerateDialogRef = ref<DialogElement | null>(null);
  const _saveJobDraftDialogRef = ref<DialogElement | null>(null);
  const _confirmScrapBookingDialogRef = ref<DialogElement | null>(null);
  const _addAddressDialogRef = ref<DialogElement | null>(null);
  const _addressBookAddressDialogRef = ref<DialogElement | null>(null);
  const _addressBookDeleteAddressDialogRef = ref<DialogElement | null>(null);

  const _eventBuses: Record<DialogName, DialogEventBus> = {
    logout: makeDialogEventBus(),
    dispatchAssignDriver: makeDialogEventBus(),
    dispatchConfirm: makeDialogEventBus(),
    arrivedDepartedStop: makeDialogEventBus(),
    chooseOptionStop: makeDialogEventBus(),
    cancelCloseStop: makeDialogEventBus(),
    newTicket: makeDialogEventBus(),
    newRFC: makeDialogEventBus(),
    cloneJob: makeDialogEventBus(),
    newQuery: makeDialogEventBus(),
    jobEditCancelJob: makeDialogEventBus(),
    jobEditChangeCustomerCode: makeDialogEventBus(),
    jobEditCloseAsFutile: makeDialogEventBus(),
    jobEditDeleteStop: makeDialogEventBus(),
    jobEditError: makeDialogEventBus(),
    jobEditLockedByOther: makeDialogEventBus(),
    jobEditLockTimeout: makeDialogEventBus(),
    jobEditNotFound: makeDialogEventBus(),
    jobEditSplitToBase: makeDialogEventBus(),
    jobEditLockJob: makeDialogEventBus(),
    jobEditUndoAllChanges: makeDialogEventBus(),
    splitToBase: makeDialogEventBus(),
    editTimestamps: makeDialogEventBus(),
    advancedSearchMultiSearch: makeDialogEventBus(),
    savedFilters: makeDialogEventBus(),
    savingCurrentSearch: makeDialogEventBus(),
    uploadingFiles: makeDialogEventBus(),
    detailedJobPhoto: makeDialogEventBus(),
    deleteJobPhoto: makeDialogEventBus(),
    print: makeDialogEventBus(),
    returnLabelsPrint: makeDialogEventBus(),
    newAirRoadUnpack: makeDialogEventBus(),
    newMessageChat: makeDialogEventBus(),
    kpiReportGenerate: makeDialogEventBus(),
    driverReportGenerate: makeDialogEventBus(),
    saveJobDraft: makeDialogEventBus(),
    confirmScrapBooking: makeDialogEventBus(),
    addAddress: makeDialogEventBus(),
    addressBookAddress: makeDialogEventBus(),
    addressBookDeleteAddress: makeDialogEventBus(),
  };

  function $reset(): void {
    Object.keys(state).forEach((dialogName) => {
      const oneDefDialogState = defDialogState[dialogName as DialogName];
      const oneDialogState = state[dialogName as DialogName];
      oneDialogState.isDialog = oneDefDialogState.isDialog;
      oneDialogState.closeDetail = oneDefDialogState.closeDetail;
      Object.assign(
        oneDialogState.dialogData,
        _cloneDeep(oneDefDialogState.dialogData),
      ); // NOTE dialogData should be flat or don't use references from the middle of the state
    });
  }

  function open<T extends FunctionName>(
    dialogName: T,
    dialogData: FunctionParams[T] = {},
  ): DialogEventBus {
    const oneDialogState = state[dialogName];
    if (oneDialogState.isDialog) {
      throw new KDialogError(`The dialog '${dialogName}' is already open`);
    }
    const oneDefDialogState = defDialogState[dialogName as DialogName];

    // $reset only this dialog when open
    oneDialogState.closeDetail = oneDefDialogState.closeDetail;
    Object.assign(
      oneDialogState.dialogData,
      _cloneDeep(oneDefDialogState.dialogData),
    );

    Object.assign(oneDialogState.dialogData, _cloneDeep(dialogData));
    oneDialogState.isDialog = true;
    _eventBuses[dialogName].confirmed = function (cb) {
      _eventBuses[dialogName].confirmedCb = cb;
      return _eventBuses[dialogName];
    };
    _eventBuses[dialogName].canceled = function (cb) {
      _eventBuses[dialogName].canceledCb = cb;
      return _eventBuses[dialogName];
    };
    return _eventBuses[dialogName];
  }

  function setDialogElement(
    dialogName: DialogName,
    element: DialogElement | null,
  ): void {
    switch (dialogName) {
      case 'logout':
        _logoutDialogRef.value = element;
        break;
      case 'dispatchAssignDriver':
        _dispatchAssignDriverDialogRef.value = element;
        break;
      case 'dispatchConfirm':
        _dispatchConfirmDialogRef.value = element;
        break;
      case 'arrivedDepartedStop':
        _arrivedDepartedStopDialogRef.value = element;
        break;
      case 'chooseOptionStop':
        _chooseOptionStopDialogRef.value = element;
        break;
      case 'cancelCloseStop':
        _cancelCloseStopDialogRef.value = element;
        break;
      case 'newTicket':
        _newTicketDialogRef.value = element;
        break;
      case 'newRFC':
        _newRFCDialogRef.value = element;
        break;
      case 'cloneJob':
        _cloneJobDialogRef.value = element;
        break;
      case 'newQuery':
        _newQueryDialogRef.value = element;
        break;
      case 'jobEditCancelJob':
        _jobEditCancelJobDialogRef.value = element;
        break;
      case 'jobEditChangeCustomerCode':
        _jobEditChangeCustomerCodeDialogRef.value = element;
        break;
      case 'jobEditCloseAsFutile':
        _jobEditCloseAsFutileDialogRef.value = element;
        break;
      case 'jobEditDeleteStop':
        _jobEditDeleteStopDialogRef.value = element;
        break;
      case 'jobEditError':
        _jobEditErrorDialogRef.value = element;
        break;
      case 'jobEditLockedByOther':
        _jobEditLockedByOtherDialogRef.value = element;
        break;
      case 'jobEditLockTimeout':
        _jobEditLockTimeoutDialogRef.value = element;
        break;
      case 'jobEditNotFound':
        _jobEditNotFoundDialogRef.value = element;
        break;
      case 'jobEditSplitToBase':
        _jobEditSplitToBaseDialogRef.value = element;
        break;
      case 'jobEditLockJob':
        _jobEditLockJobDialogRef.value = element;
        break;
      case 'jobEditUndoAllChanges':
        _jobEditUndoAllChangesDialogRef.value = element;
        break;
      case 'splitToBase':
        _splitToBaseDialogRef.value = element;
        break;
      case 'editTimestamps':
        _editTimestampsDialogRef.value = element;
        break;
      case 'advancedSearchMultiSearch':
        _advancedSearchMultiSearchDialogRef.value = element;
        break;
      case 'savedFilters':
        _savedFiltersDialogRef.value = element;
        break;
      case 'savingCurrentSearch':
        _savingCurrentSearchDialogRef.value = element;
        break;
      case 'uploadingFiles':
        _uploadingFilesDialogRef.value = element;
        break;
      case 'detailedJobPhoto':
        _detailedJobPhotoDialogRef.value = element;
        break;
      case 'deleteJobPhoto':
        _deleteJobPhotoDialogRef.value = element;
        break;
      case 'print':
        _printDialogRef.value = element;
        break;
      case 'returnLabelsPrint':
        _returnLabelsPrintDialogRef.value = element;
        break;
      case 'newAirRoadUnpack':
        _newAirRoadUnpackDialogRef.value = element;
        break;
      case 'newMessageChat':
        _newMessageChatDialogRef.value = element;
        break;
      case 'kpiReportGenerate':
        _kpiReportGenerateDialogRef.value = element;
        break;
      case 'driverReportGenerate':
        _driverReportGenerateDialogRef.value = element;
        break;
      case 'saveJobDraft':
        _saveJobDraftDialogRef.value = element;
        break;
      case 'confirmScrapBooking':
        _confirmScrapBookingDialogRef.value = element;
        break;
      case 'addAddress':
        _addAddressDialogRef.value = element;
        break;
      case 'addressBookAddress':
        _addressBookAddressDialogRef.value = element;
        break;
      case 'addressBookDeleteAddress':
        _addressBookDeleteAddressDialogRef.value = element;
        break;
      default:
        dialogName satisfies never;
        throw new KDialogError('Wrong dialog name');
    }
    return undefined;
  }

  function getDialogElement(dialogName: DialogName): DialogElement | null {
    let dialogElement;
    switch (dialogName) {
      case 'logout':
        dialogElement = _logoutDialogRef.value;
        break;
      case 'dispatchAssignDriver':
        dialogElement = _dispatchAssignDriverDialogRef.value;
        break;
      case 'dispatchConfirm':
        dialogElement = _dispatchConfirmDialogRef.value;
        break;
      case 'arrivedDepartedStop':
        dialogElement = _arrivedDepartedStopDialogRef.value;
        break;
      case 'chooseOptionStop':
        dialogElement = _chooseOptionStopDialogRef.value;
        break;
      case 'cancelCloseStop':
        dialogElement = _cancelCloseStopDialogRef.value;
        break;
      case 'newTicket':
        dialogElement = _newTicketDialogRef.value;
        break;
      case 'newRFC':
        dialogElement = _newRFCDialogRef.value;
        break;
      case 'cloneJob':
        dialogElement = _cloneJobDialogRef.value;
        break;
      case 'newQuery':
        dialogElement = _newQueryDialogRef.value;
        break;
      case 'jobEditCancelJob':
        dialogElement = _jobEditCancelJobDialogRef.value;
        break;
      case 'jobEditChangeCustomerCode':
        dialogElement = _jobEditChangeCustomerCodeDialogRef.value;
        break;
      case 'jobEditCloseAsFutile':
        dialogElement = _jobEditCloseAsFutileDialogRef.value;
        break;
      case 'jobEditDeleteStop':
        dialogElement = _jobEditDeleteStopDialogRef.value;
        break;
      case 'jobEditError':
        dialogElement = _jobEditErrorDialogRef.value;
        break;
      case 'jobEditLockedByOther':
        dialogElement = _jobEditLockedByOtherDialogRef.value;
        break;
      case 'jobEditLockTimeout':
        dialogElement = _jobEditLockTimeoutDialogRef.value;
        break;
      case 'jobEditNotFound':
        dialogElement = _jobEditNotFoundDialogRef.value;
        break;
      case 'jobEditSplitToBase':
        dialogElement = _jobEditSplitToBaseDialogRef.value;
        break;
      case 'jobEditLockJob':
        dialogElement = _jobEditLockJobDialogRef.value;
        break;
      case 'jobEditUndoAllChanges':
        dialogElement = _jobEditUndoAllChangesDialogRef.value;
        break;
      case 'splitToBase':
        dialogElement = _splitToBaseDialogRef.value;
        break;
      case 'editTimestamps':
        dialogElement = _editTimestampsDialogRef.value;
        break;
      case 'advancedSearchMultiSearch':
        dialogElement = _advancedSearchMultiSearchDialogRef.value;
        break;
      case 'savedFilters':
        dialogElement = _savedFiltersDialogRef.value;
        break;
      case 'savingCurrentSearch':
        dialogElement = _savingCurrentSearchDialogRef.value;
        break;
      case 'uploadingFiles':
        dialogElement = _uploadingFilesDialogRef.value;
        break;
      case 'detailedJobPhoto':
        dialogElement = _detailedJobPhotoDialogRef.value;
        break;
      case 'deleteJobPhoto':
        dialogElement = _deleteJobPhotoDialogRef.value;
        break;
      case 'print':
        dialogElement = _printDialogRef.value;
        break;
      case 'returnLabelsPrint':
        dialogElement = _returnLabelsPrintDialogRef.value;
        break;
      case 'newAirRoadUnpack':
        dialogElement = _newAirRoadUnpackDialogRef.value;
        break;
      case 'newMessageChat':
        dialogElement = _newMessageChatDialogRef.value;
        break;
      case 'kpiReportGenerate':
        dialogElement = _kpiReportGenerateDialogRef.value;
        break;
      case 'driverReportGenerate':
        dialogElement = _driverReportGenerateDialogRef.value;
        break;
      case 'saveJobDraft':
        dialogElement = _saveJobDraftDialogRef.value;
        break;
      case 'confirmScrapBooking':
        dialogElement = _confirmScrapBookingDialogRef.value;
        break;
      case 'addAddress':
        dialogElement = _addAddressDialogRef.value;
        break;
      case 'addressBookAddress':
        dialogElement = _addressBookAddressDialogRef.value;
        break;
      case 'addressBookDeleteAddress':
        dialogElement = _addressBookDeleteAddressDialogRef.value;
        break;
      default:
        dialogName satisfies never;
        throw new KDialogError('Wrong dialog name');
    }
    return dialogElement;
  }

  // another option
  // https://v3-migration.vuejs.org/breaking-changes/events-api.html#event-bus
  // how EventTarget is dealing with once
  // https://github.com/developit/mitt/issues/136#issuecomment-867111046
  function getDialogEmitter(dialogName: DialogName) {
    const dialogElement = getDialogElement(dialogName);
    return dialogElement?.$el as Element | undefined;
  }

  async function close(dialogName: DialogName) {
    const oneDialogState = state[dialogName];
    if (!oneDialogState.isDialog) return;
    oneDialogState.closeDetail = 'cancel';
    if (_eventBuses[dialogName].canceledCb) {
      await _eventBuses[dialogName].canceledCb();
      delete _eventBuses[dialogName].canceledCb;
    }
    oneDialogState.isDialog = false;
  }

  async function handleCancelClick(event: Event, dialogName: DialogName) {
    await close(dialogName);
  }

  async function _handleConfirmClick(
    dialogName: DialogName,
    promise: () => Promise<unknown>,
  ) {
    const oneDialogState = state[dialogName];

    if (!('isConfirmLoading' in oneDialogState.dialogData)) return; // TODO use `satisfies` or something
    if (oneDialogState.dialogData.isConfirmLoading) return;

    oneDialogState.dialogData.isConfirmLoading = true;

    await promise()
      .then(async () => {
        oneDialogState.closeDetail = 'confirm';
        if (_eventBuses[dialogName].confirmedCb) {
          await _eventBuses[dialogName].confirmedCb();
          delete _eventBuses[dialogName].confirmedCb;
        }
        oneDialogState.isDialog = false;
      })
      .finally(() => {
        if (!('isConfirmLoading' in oneDialogState.dialogData)) return; // TODO use `satisfies` or something
        oneDialogState.dialogData.isConfirmLoading = false;
      });
  }

  async function handleLogoutDialogConfirmClick(
    event: Event,
    props: LogoutDialogDefProps,
  ) {
    const promise = async function () {
      console.log(props);
      if (!props.confirm) return;
      return props.confirm();
    };
    await _handleConfirmClick('logout', promise);
  }

  async function handleDispatchAssignDriverDialogConfirmClick(
    event: SubmitEventPromise | null | undefined,
    formData: DispatchAssignDriverDialogFormValidData,
    props: DispatchAssignDriverDialogDefProps,
  ): Promise<void> {
    if (!props.jobs.length) return;

    const promise = async function () {
      // Bulk: assign every listed job to the chosen driver with the requested
      // action; one summary toast reports the outcome.
      await fetchPostJobAllocationBulk(
        props.jobs.map((job) => ({
          action: props.action,
          job_id: job.jobId,
          job_date: job.jobDate,
          driver: formData.driver,
        })),
      );
    };
    await _handleConfirmClick('dispatchAssignDriver', promise);
  }

  async function handleDispatchConfirmDialogConfirmClick(
    event: Event,
    props: DispatchConfirmDialogDefProps,
  ): Promise<void> {
    // TODO bulk action endpoints
    const job = props.jobs[0];
    if (!job) return; // TODO TypeScript noUncheckedIndexedAccess https://stackoverflow.com/a/50647536/1054723

    const promise = async function () {
      if (!props.confirm) return;
      return props.confirm();
    };
    await _handleConfirmClick('dispatchConfirm', promise);
  }

  async function handleArrivedDepartedStopDialogConfirmClick(
    event: Event | SubmitEventPromise | null | undefined,
    formData: DashboardStopDialogFormValidData | undefined,
    props: ArrivedDepartedStopDialogDefProps,
  ): Promise<void> {
    const promise = async function () {
      if (!props.confirm) return;
      return props.confirm(formData);
    };
    await _handleConfirmClick('arrivedDepartedStop', promise);
  }

  async function handleChooseOptionStopDialogConfirmClick(
    event: SubmitEventPromise | null | undefined,
    formData: ChooseOptionStopDialogFormValidData,
    props: ChooseOptionStopDialogDefProps,
  ): Promise<void> {
    const promise = async function () {
      if (!props.confirm) return;
      return props.confirm(formData);
    };
    await _handleConfirmClick('chooseOptionStop', promise);
  }

  async function handleCancelCloseStopDialogConfirmClick(
    event: SubmitEventPromise | null | undefined,
    formData: CancelCloseStopDialogFormValidData,
    props: CancelCloseStopDialogDefProps,
  ): Promise<void> {
    // TODO bulk action endpoints
    const job = props.jobs[0];
    if (!job) return; // TODO TypeScript noUncheckedIndexedAccess https://stackoverflow.com/a/50647536/1054723

    let promise;
    if (props.type === 'cancel') {
      const params: openapi.JobCancelContentOpenapi = {
        job_id: job.jobId,
        job_date: job.jobDate,
        actor: formData.name,
        reason: formData.reason,
      };
      promise = async function () {
        return fetchPostJobCancel(params);
      };
    } else if (props.type === 'futile') {
      const params: openapi.JobFutileCreateBodyOpenapi = {
        job_id: job.jobId,
        job_date: job.jobDate,
        driver: job.driver || NaN, // TODO
        reason: formData.reason,
      };
      promise = async function () {
        return fetchPostJobFutile(params);
      };
    } else {
      props.type satisfies never;
      throw new KDialogError('Wrong dialog type');
    }
    await _handleConfirmClick('cancelCloseStop', promise);
  }

  async function handleNewTicketDialogConfirmClick(
    event: SubmitEventPromise | null | undefined,
    formData: NewTicketDialogFormValidData,
    props: NewTicketDialogDefProps,
  ): Promise<void> {
    const user = userStore.user;
    if (!user.email) return;
    if (!props.jobId) return;
    if (!props.jobDate) return;
    if (!formData.message) return;

    let group = 'IT Support';
    if (import.meta.env.VITE_APP_SENTRY_ENVIRONMENT === 'production') {
      group = 'Service Delivery Team';
    }
    const bodyNote = `
      <div>
        <h3>
          Job #<a href='https://portal.kkts.com.au/job/${props.jobId}'>${props.jobId}</a> at ${props.jobDate}
        </h3>
        <br />
        <span>Message:</span>
        <br />
        <div>${formData.message as string}</div>
      </div>
    `;
    const attachments: openapi.ZammadArticleOpenapi['attachments'] = [];
    if (formData.attachments) {
      for (const file of formData.attachments) {
        attachments.push(await getBase64FileForZammad(file));
      }
    }
    const isStaff = ability.can('staff', 'Job');
    const params: openapi.JobCreateTicketBodyOpenapi = {
      title: formData.subject,
      group,
      customer: user.email.toLowerCase(),
      article: {
        subject: formData.subject,
        body: bodyNote,
        type: isStaff ? 'email' : 'note',
        content_type: 'text/html',
        sender: isStaff ? 'Agent' : 'Customer',
        internal: false,
        attachments,
        to: isStaff
          ? cleanEmailString(formData.to, props.channelsEmails).join(', ')
          : undefined,
        cc:
          formData.cc && isStaff
            ? cleanEmailString(formData.cc, props.channelsEmails).join(', ')
            : undefined,
      },
      rh_job: props.jobId,
      is_rfc: false,
    };
    const promise = async function () {
      return fetchPostZammadTickets(params);
    };
    await _handleConfirmClick('newTicket', promise);
  }

  async function handleNewRFCDialogConfirmClick(
    event: SubmitEventPromise | null | undefined,
    formData: NewRFCDialogFormValidData,
    props: NewRFCDialogDefProps,
  ): Promise<void> {
    const user = userStore.user;
    if (!user.email) return;
    if (!props.jobId) return;
    if (!props.jobDate) return;
    if (!formData.message) return;

    let group = 'IT Support';
    if (import.meta.env.VITE_APP_SENTRY_ENVIRONMENT === 'production') {
      group = 'Service Delivery Team';
    }
    const bodyRfc = `
      <div>
        <h3>
          A new request for change of the Job #<a href='https://portal.kkts.com.au/job/${props.jobId}'>${props.jobId}</a> at ${props.jobDate}
        </h3>
        <br/>
        <span>The RFC message:</span>
        <br/>
        <div>${formData.message as string}</div>
      </div>
    `;
    const attachments: openapi.ZammadArticleOpenapi['attachments'] = [];
    if (formData.attachments) {
      for (const file of formData.attachments) {
        attachments.push(await getBase64FileForZammad(file));
      }
    }
    const isStaff = ability.can('staff', 'Job');
    const params: openapi.JobCreateTicketBodyOpenapi = {
      title: formData.subject,
      group,
      customer: user.email.toLowerCase(),
      article: {
        subject: `[RFC] ${formData.subject as string}`,
        body: bodyRfc,
        type: 'note',
        content_type: 'text/html',
        sender: isStaff ? 'Agent' : 'Customer',
        internal: false,
        attachments,
      },
      rh_job: props.jobId,
      is_rfc: true,
    };
    const promise = async function () {
      return fetchPostZammadTickets(params);
    };
    await _handleConfirmClick('newRFC', promise);
  }

  async function handleCloneJobDialogConfirmClick(
    event: Event,
    props: CloneJobDialogDefProps,
  ): Promise<void> {
    // Navigation to the prefilled Advanced JobBooking form happens on the caller's `closed` handler.
    const promise = async function () {
      return undefined;
    };
    await _handleConfirmClick('cloneJob', promise);
  }

  async function handleNewQueryDialogConfirmClick(
    event: SubmitEventPromise | null | undefined,
    formData: NewQueryDialogFormValidData,
    props: NewQueryDialogDefProps,
  ): Promise<void> {
    const user = userStore.user;
    if (!user.username) return;
    if (!props.job) return;
    if (!formData.message) return;

    const params: openapi.DriverSendMessageCreateBodyOpenapi = {
      job_key: props.job.rockhopper_key,
      job_date: props.job.rockhopper_date,
      message: formData.message,
      respond_to: user.username.slice(0, 20),
      respond_email: user.email || '',
    };

    const promise = async function () {
      return fetchPostJobMessage(params);
    };
    await _handleConfirmClick('newQuery', promise);
  }

  async function handleSplitToBaseDialogConfirmClick(
    event: SubmitEventPromise | null | undefined,
    formData: SplitToBaseDialogFormValidData,
    props: SplitToBaseDialogDefProps,
  ): Promise<void> {
    if (!props.job) return;
    if (!formData.warehouse) return;

    const params: openapi.JobAllocationCreateBodyOpenapi = {
      action: 'tobase',
      job_id: props.job.rockhopper_id,
      job_date: props.job.rockhopper_date,
      driver: props.job.current_driver_code || 0,
      stop_number: 2,
      // TODO
      // @ts-expect-error
      base: kktsSplitToBase.indexOf(formData.warehouse) + 1 || 0,
    };

    const promise = async function () {
      return fetchPostJobAllocation(params);
    };
    await _handleConfirmClick('splitToBase', promise);
  }

  async function handleEditTimestampsDialogConfirmClick(
    event: SubmitEventPromise | null | undefined,
    formData: EditTimestampsDialogFormValidData,
    props: EditTimestampsDialogDefProps,
  ): Promise<void> {
    const promise = async function () {
      return successDelay(); // TODO
    };
    await _handleConfirmClick('editTimestamps', promise);
  }

  async function handleAdvancedSearchMultiSearchDialogConfirmClick(
    event: SubmitEventPromise | null | undefined,
    formData: AdvancedSearchMultiSearchDialogFormValidData,
    props: AdvancedSearchMultiSearchDialogDefProps,
  ): Promise<void> {
    const promise = async function () {
      if (!props.confirm) return;
      return props.confirm(formData);
    };
    await _handleConfirmClick('advancedSearchMultiSearch', promise);
  }

  async function handleSavingCurrentSearchDialogConfirmClick(
    event: SubmitEventPromise | null | undefined,
    formData: SavingCurrentSearchDialogFormValidData,
    props: SavingCurrentSearchDialogDefProps,
  ) {
    const promise = async function () {
      if (!props.confirm) return;
      return props.confirm(formData);
    };
    await _handleConfirmClick('savingCurrentSearch', promise);
  }

  async function handleUploadingFilesDialogConfirmClick(
    event: SubmitEventPromise | null | undefined,
    formData: UploadingFilesDialogFormValidData,
    props: UploadingFilesDialogDefProps,
  ) {
    const promise = async function () {
      if (!props.confirm) return;
      return props.confirm(formData);
    };
    await _handleConfirmClick('uploadingFiles', promise);
  }

  async function handleDeleteJobPhotoDialogConfirmClick(
    event: Event,
    props: DeleteJobPhotoDialogDefProps,
  ) {
    const promise = async function () {
      if (!props.confirm) return;
      return props.confirm();
    };
    await _handleConfirmClick('deleteJobPhoto', promise);
  }

  async function handlePrintDialogConfirmClick(
    event: Event,
    selectedPrinterId: number,
    props: PrintDialogDefProps,
  ) {
    if (!props.arJobId) return;
    if (!selectedPrinterId) return;
    const params = {
      arJobId: props.arJobId,
      printerId: selectedPrinterId,
    };
    const promise = async function () {
      return fetchPostAirroadJobsByIdPrintLabel(params);
    };
    await _handleConfirmClick('print', promise);
  }

  async function handleReturnLabelsPrintDialogConfirmClick(
    event: SubmitEventPromise | null | undefined,
    formData: ReturnLabelsPrintDialogFormValidData,
    selectedPrinterId: number,
    props: ReturnLabelsPrintDialogDefProps,
  ) {
    if (!props.drivers.length) return;
    if (!selectedPrinterId) return;
    const promise = async function () {
      return fetchPostReportReturnLabelsPrint({
        drivers: props.drivers,
        size: formData.size,
        printerId: selectedPrinterId,
        copies: formData.copies,
      });
    };
    await _handleConfirmClick('returnLabelsPrint', promise);
  }

  async function handleNewAirRoadUnpackDialogConfirmClick(
    event: Event | SubmitEventPromise | null | undefined,
    formData: NewAirRoadUnpackDialogFormValidData | undefined,
    props: NewAirRoadUnpackDialogDefProps,
  ) {
    const promise = async function () {
      if (!props.confirm) return;
      return props.confirm(
        props.packageNum,
        props.productNum,
        props.addProductId,
        formData,
      );
    };
    await _handleConfirmClick('newAirRoadUnpack', promise);
  }

  async function handleNewMessageChatDialogConfirmClick(
    event: SubmitEventPromise | null | undefined,
    formData: NewMessageChatDialogFormValidData,
    props: NewMessageChatDialogDefProps,
  ) {
    const promise = async function () {
      if (!props.confirm) return;
      return props.confirm(formData);
    };
    await _handleConfirmClick('newMessageChat', promise);
  }

  async function handleKpiReportGenerateDialogConfirmClick(
    event: SubmitEventPromise | null | undefined,
    formData: KpiReportGenerateDialogFormValidData,
    props: KpiReportGenerateDialogDefProps,
  ) {
    const promise = async function () {
      if (!props.confirm) return;
      return props.confirm(formData);
    };
    await _handleConfirmClick('kpiReportGenerate', promise);
  }

  async function handleDriverReportGenerateDialogConfirmClick(
    event: SubmitEventPromise | null | undefined,
    formData: DriverReportGenerateDialogFormValidData,
    props: DriverReportGenerateDialogDefProps,
  ) {
    const promise = async function () {
      if (!props.confirm) return;
      return props.confirm(formData);
    };
    await _handleConfirmClick('driverReportGenerate', promise);
  }

  async function handleAddAddressDialogConfirmClick(
    event: SubmitEventPromise | null | undefined,
    formData: AddAddressDialogFormValidData,
    props: AddAddressDialogDefProps,
  ) {
    const promise = async function () {
      if (!props.confirm) return;
      return props.confirm(formData);
    };
    await _handleConfirmClick('addAddress', promise);
  }

  async function handleAddressBookAddressDialogConfirmClick(
    event: SubmitEventPromise | null | undefined,
    formData: AddressBookAddressDialogFormValidData,
    props: AddressBookAddressDialogDefProps,
  ) {
    const promise = async function () {
      if (!props.confirm) return;
      return props.confirm(formData);
    };
    await _handleConfirmClick('addressBookAddress', promise);
  }

  async function handleAddressBookDeleteAddressDialogConfirmClick(
    event: Event,
    props: AddressBookDeleteAddressDialogDefProps,
  ) {
    const promise = async function () {
      if (!props.confirm) return;
      return props.confirm();
    };
    await _handleConfirmClick('addressBookDeleteAddress', promise);
  }

  async function handleSaveJobDraftDialogConfirmClick(
    event: Event,
    name: string,
    mode: SaveDraftMode,
    props: SaveJobDraftDialogDefProps,
  ) {
    const promise = async function () {
      if (!props.confirm) return;
      return props.confirm(name, mode);
    };
    await _handleConfirmClick('saveJobDraft', promise);
  }

  async function handleConfirmScrapBookingDialogConfirmClick() {
    // Resolution is delivered through the dialog's event bus
    // (`.confirmed()` / `.canceled()`); this only drives the open/close state.
    await _handleConfirmClick('confirmScrapBooking', () => Promise.resolve());
  }

  async function handleJobEditCancelJobDialogConfirmClick(
    event: SubmitEventPromise | null | undefined,
    formData: JobEditCancelJobDialogFormValidData,
    props: JobEditCancelJobDialogDefProps,
  ): Promise<void> {
    const promise = async function () {
      if (!props.confirm) return;
      return props.confirm(formData);
    };
    await _handleConfirmClick('jobEditCancelJob', promise);
  }

  async function handleJobEditChangeCustomerCodeDialogConfirmClick(
    event: SubmitEventPromise | null | undefined,
    formData: JobEditChangeCustomerCodeDialogFormValidData,
    props: JobEditChangeCustomerCodeDialogDefProps,
  ): Promise<void> {
    const promise = async function () {
      if (!props.confirm) return;
      return props.confirm(formData);
    };
    await _handleConfirmClick('jobEditChangeCustomerCode', promise);
  }

  async function handleJobEditCloseAsFutileDialogConfirmClick(
    event: SubmitEventPromise | null | undefined,
    formData: JobEditCloseAsFutileDialogFormValidData,
    props: JobEditCloseAsFutileDialogDefProps,
  ): Promise<void> {
    const promise = async function () {
      if (!props.confirm) return;
      return props.confirm(formData);
    };
    await _handleConfirmClick('jobEditCloseAsFutile', promise);
  }

  async function handleJobEditDeleteStopDialogConfirmClick(
    event: Event,
    props: JobEditDeleteStopDialogDefProps,
  ): Promise<void> {
    const promise = async function () {
      if (!props.confirm) return;
      return props.confirm();
    };
    await _handleConfirmClick('jobEditDeleteStop', promise);
  }

  async function handleJobEditSplitToBaseDialogConfirmClick(
    event: SubmitEventPromise | null | undefined,
    formData: JobEditSplitToBaseDialogFormValidData,
    props: JobEditSplitToBaseDialogDefProps,
  ): Promise<void> {
    const promise = async function () {
      if (!props.confirm) return;
      return props.confirm(formData);
    };
    await _handleConfirmClick('jobEditSplitToBase', promise);
  }

  async function handleJobEditLockJobDialogConfirmClick(
    event: Event,
    props: JobEditLockJobDialogDefProps,
  ): Promise<void> {
    const promise = async function () {
      if (!props.confirm) return;
      return props.confirm();
    };
    await _handleConfirmClick('jobEditLockJob', promise);
  }

  async function handleJobEditUndoAllChangesDialogConfirmClick(
    event: Event,
    props: JobEditUndoAllChangesDialogDefProps,
  ): Promise<void> {
    const promise = async function () {
      if (!props.confirm) return;
      return props.confirm();
    };
    await _handleConfirmClick('jobEditUndoAllChanges', promise);
  }

  async function handleJobEditLockTimeoutDialogContinueClick(
    event: Event,
    props: JobEditLockTimeoutDialogDefProps,
  ): Promise<void> {
    const promise = async function () {
      if (!props.confirm) return;
      return props.confirm();
    };
    await _handleConfirmClick('jobEditLockTimeout', promise);
  }

  async function handleJobEditErrorDialogCancelClick(
    event: Event,
    props: JobEditErrorDialogDefProps,
  ): Promise<void> {
    const promise = async function () {
      if (!props.cancelCallback) return;
      return props.cancelCallback();
    };
    await _handleConfirmClick('jobEditError', promise);
  }

  async function handleJobEditErrorDialogTryAgainClick(
    event: Event,
    props: JobEditErrorDialogDefProps,
  ): Promise<void> {
    const promise = async function () {
      if (!props.retryCallback) return;
      return props.retryCallback();
    };
    await _handleConfirmClick('jobEditError', promise);
  }

  function _getIsDialogWatchSource(dialogName: DialogName) {
    const oneDialogState = state[dialogName];
    return function () {
      return oneDialogState.isDialog;
    };
  }

  function _getIsDialogWatchCallback(dialogName: DialogName) {
    const oneDialogState = state[dialogName];
    return function () {
      const dialogElement = getDialogElement(dialogName);
      // it seems that `dispatchEvent` is not so convenient to use
      if (oneDialogState.isDialog) {
        dialogElement?.$el.dispatchEvent(new Event('opened'));
        _eventBuses[dialogName].emit('opened');
      } else {
        const event = new CustomEvent('closed', {
          detail: oneDialogState.closeDetail,
        });
        dialogElement?.$el.dispatchEvent(event);
        // TODO once('closed', function(closeDetail: TYPE THIS) {
        _eventBuses[dialogName].emit('closed', oneDialogState.closeDetail);
      }
    };
  }

  Object.keys(state).forEach((dialogName) => {
    watch(
      _getIsDialogWatchSource(dialogName as DialogName),
      _getIsDialogWatchCallback(dialogName as DialogName),
    );
  });

  return {
    ...toRefs(state),
    $reset,
    open,
    setDialogElement,
    getDialogElement,
    getDialogEmitter,
    close,
    handleCancelClick,
    handleLogoutDialogConfirmClick,
    handleDispatchAssignDriverDialogConfirmClick,
    handleDispatchConfirmDialogConfirmClick,
    handleArrivedDepartedStopDialogConfirmClick,
    handleChooseOptionStopDialogConfirmClick,
    handleCancelCloseStopDialogConfirmClick,
    handleNewTicketDialogConfirmClick,
    handleNewRFCDialogConfirmClick,
    handleCloneJobDialogConfirmClick,
    handleNewQueryDialogConfirmClick,
    handleJobEditCancelJobDialogConfirmClick,
    handleJobEditChangeCustomerCodeDialogConfirmClick,
    handleJobEditCloseAsFutileDialogConfirmClick,
    handleJobEditDeleteStopDialogConfirmClick,
    handleJobEditErrorDialogCancelClick,
    handleJobEditErrorDialogTryAgainClick,
    handleJobEditLockTimeoutDialogContinueClick,
    handleJobEditSplitToBaseDialogConfirmClick,
    handleJobEditLockJobDialogConfirmClick,
    handleJobEditUndoAllChangesDialogConfirmClick,
    handleSplitToBaseDialogConfirmClick,
    handleEditTimestampsDialogConfirmClick,
    handleAdvancedSearchMultiSearchDialogConfirmClick,
    handleSavingCurrentSearchDialogConfirmClick,
    handleUploadingFilesDialogConfirmClick,
    handleDeleteJobPhotoDialogConfirmClick,
    handlePrintDialogConfirmClick,
    handleReturnLabelsPrintDialogConfirmClick,
    handleNewAirRoadUnpackDialogConfirmClick,
    handleNewMessageChatDialogConfirmClick,
    handleKpiReportGenerateDialogConfirmClick,
    handleDriverReportGenerateDialogConfirmClick,
    handleAddAddressDialogConfirmClick,
    handleAddressBookAddressDialogConfirmClick,
    handleAddressBookDeleteAddressDialogConfirmClick,
    handleSaveJobDraftDialogConfirmClick,
    handleConfirmScrapBookingDialogConfirmClick,
  };
}

export const useDialogStore = defineStore('dialog', dialogStoreSetup);
